import getConfig from '@src/util/getConfig';

/**
 * Browser-side log shipping to Better Stack (GRD-133).
 *
 * Sentry captures exceptions and PostHog captures behaviour, but neither gives
 * support a queryable log stream, and the backend now ships one. This is the
 * matching browser half, so a failed request can be read next to the backend
 * line that served it — joined on the `x-request-id` both sides carry.
 *
 * Use its own Better Stack source, NOT the backend's: this token ships inside
 * the public bundle, so it has to be rotatable without interrupting backend
 * ingestion, and the two streams are more useful queried apart.
 *
 * Config resolution mirrors axiosConfig.jsx — build-time env first so this can
 * be switched on before the Directus config gains the fields, then CMS config.
 * With neither set, every function here is a no-op.
 */

const DISABLED_HOSTS = ['localhost', '127.0.0.1'];

/** Ship at most this many events per request. */
const BATCH_SIZE = 50;
const FLUSH_INTERVAL_MS = 5000;
/** Bounded so a broken endpoint can never grow the tab's memory. */
const MAX_QUEUED = 500;

type LogLevel = 'info' | 'warn' | 'error';

interface LogEntry {
    dt: string;
    level: LogLevel;
    message: string;
    service: 'guardian-frontend';
    [key: string]: unknown;
}

let ingestUrl: string | null = null;
let token: string | null = null;
let initialized = false;
let queue: LogEntry[] = [];

export function isBetterStackEnabled(): boolean {
    return initialized;
}

function post(batch: LogEntry[]): void {
    if (!ingestUrl || !token || batch.length === 0) return;
    try {
        // `keepalive` (not sendBeacon) on purpose: Better Stack authorises
        // ingestion only via the `Authorization: Bearer` header, and sendBeacon
        // cannot set headers. keepalive survives page unload and does allow
        // them. Its budget is 64KB per request, which BATCH_SIZE stays well
        // under for URL/status-shaped lines.
        fetch(ingestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(batch),
            keepalive: true,
        }).catch(() => {});
    } catch {
        // Logging must never surface to the user or break a page.
    }
}

function flush(): void {
    if (queue.length === 0) return;
    const batch = queue;
    queue = [];
    post(batch);
}

/**
 * Queue one structured line. Safe to call before init (drops silently) and
 * safe to call during SSR.
 */
export function sendLog(
    level: LogLevel,
    message: string,
    context: Record<string, unknown> = {},
): void {
    if (!initialized || typeof window === 'undefined') return;
    try {
        if (queue.length >= MAX_QUEUED) return;
        queue.push({
            dt: new Date().toISOString(),
            level,
            message,
            service: 'guardian-frontend',
            path: window.location?.pathname,
            ...context,
        });
        if (queue.length >= BATCH_SIZE) flush();
    } catch {
        // Never let instrumentation throw into application code.
    }
}

export function initBetterStack(): void {
    if (typeof window === 'undefined' || initialized) return;

    const config = getConfig();
    const url = process.env.GATSBY_BETTERSTACK_LOGS_URL || config.betterstack_logs_url;
    const sourceToken = process.env.GATSBY_BETTERSTACK_TOKEN || config.betterstack_token;

    if (!url || !sourceToken) return;

    const { hostname } = window.location;
    const isDisabled = DISABLED_HOSTS.some((h) => hostname.includes(h)) || hostname.includes('dev.');
    if (isDisabled) return;

    ingestUrl = url;
    token = sourceToken;
    initialized = true;

    setInterval(() => flush(), FLUSH_INTERVAL_MS);

    // pagehide fires on mobile Safari where unload does not.
    window.addEventListener('pagehide', () => flush());
    window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
    });
}
